import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, CheckCircle, Clock, Activity, Database, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { blink } from '../lib/blink';

interface SyncStatus {
  accountId: string;
  provider: string;
  emailAddress: string;
  lastSyncAt: string | null;
  syncEnabled: boolean;
  syncStatus: string;
  tokenExpiresAt: string | null;
  historyId: string | null;
}

interface SyncError {
  id: string;
  accountId: string;
  errorType: string;
  errorMessage: string;
  errorDetails: string | null;
  createdAt: string;
}

const SyncAdmin: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [syncErrors, setSyncErrors] = useState<SyncError[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load user
  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      setUser(state.user);
      if (state.user) {
        loadSyncData(state.user.id);
      } else {
        setIsLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  // Also trigger calendar sync to populate calendar_events_enhanced
  const triggerCalendarSync = async (userId: string) => {
    try {
      const token = localStorage.getItem('access-token');
      if (!token) return;

      // Call calendar sync function
      await fetch('https://ben3ou87--getCalendarEventsByWeek.functions.blink.new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.log('Calendar sync trigger skipped:', error);
    }
  };

  // Load sync status and errors
  const loadSyncData = async (userId: string) => {
    try {
      setIsLoading(true);

      // Load email accounts
      const accounts = await blink.db.emailAccounts.list({
        where: { userId },
        orderBy: { createdAt: 'desc' },
        limit: 50
      });

      const statuses: SyncStatus[] = accounts.map((acc: any) => ({
        accountId: acc.id,
        provider: acc.provider,
        emailAddress: acc.emailAddress || acc.email_address,
        lastSyncAt: acc.lastSyncAt || acc.last_sync_at,
        syncEnabled: Number(acc.syncEnabled || acc.sync_enabled) > 0,
        syncStatus: acc.syncStatus || acc.sync_status || 'unknown',
        tokenExpiresAt: acc.tokenExpiresAt || acc.token_expires_at,
        historyId: acc.historyId || acc.history_id
      }));

      setSyncStatuses(statuses);

      // Load recent sync errors
      const errors = await blink.db.syncErrors.list({
        orderBy: { createdAt: 'desc' },
        limit: 100
      });

      const formattedErrors: SyncError[] = errors.map((err: any) => ({
        id: err.id,
        accountId: err.accountId || err.account_id,
        errorType: err.errorType || err.error_type,
        errorMessage: err.errorMessage || err.error_message,
        errorDetails: err.errorDetails || err.error_details,
        createdAt: err.createdAt || err.created_at
      }));

      setSyncErrors(formattedErrors);
    } catch (error) {
      console.error('Failed to load sync data:', error);
      toast.error('Failed to load sync status');
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh data
  const handleRefresh = async () => {
    if (!user) return;
    setIsRefreshing(true);
    await loadSyncData(user.id);
    // Also trigger calendar sync
    await triggerCalendarSync(user.id);
    setIsRefreshing(false);
    toast.success('Sync data refreshed');
  };

  // Trigger manual sync
  const triggerSync = async (accountId: string, provider: string) => {
    if (!user) return;

    try {
      toast.info(`Triggering ${provider} sync...`);

      const account = syncStatuses.find(s => s.accountId === accountId);
      if (!account) {
        toast.error('Account not found');
        return;
      }

      // Get valid access token
      const tokenResponse = await fetch('https://ben3ou87--oauth-refresh.functions.blink.new', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider,
          userId: user.id
        })
      });

      if (!tokenResponse.ok) {
        toast.error('Failed to get access token');
        return;
      }

      const { accessToken } = await tokenResponse.json();

      // Trigger sync
      const syncUrl = provider === 'gmail' 
        ? 'https://ben3ou87--gmail-sync.functions.blink.new'
        : 'https://ben3ou87--outlook-sync.functions.blink.new';

      const syncResponse = await fetch(syncUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken,
          userId: user.id,
          accountId,
          maxResults: 50
        })
      });

      if (syncResponse.ok) {
        const data = await syncResponse.json();
        toast.success(`Synced ${data.synced || 0} emails`);
        await loadSyncData(user.id);
      } else {
        const error = await syncResponse.json();
        toast.error(`Sync failed: ${error.error}`);
      }
    } catch (error) {
      console.error('Sync trigger failed:', error);
      toast.error('Failed to trigger sync');
    }
  };

  // Format timestamp
  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = Date.now();
    const diff = now - date.getTime();
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleString();
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'syncing': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'idle': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'error': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Please sign in to view sync admin</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading sync data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Sync Administration</h2>
          <p className="text-muted-foreground">Monitor email and calendar sync status (Admin Only)</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Last Synced Info */}
          {syncStatuses.length > 0 && syncStatuses[0].lastSyncAt && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Last synced: {formatTime(syncStatuses[0].lastSyncAt)}
            </div>
          )}
          <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh All
          </Button>
        </div>
      </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Database className="h-4 w-4 text-blue-500" />
                Total Accounts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{syncStatuses.length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {syncStatuses.filter(s => s.syncEnabled).length} enabled
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="h-4 w-4 text-green-500" />
                Active Syncs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {syncStatuses.filter(s => s.syncStatus === 'syncing').length}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Currently syncing</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                Recent Errors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{syncErrors.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Last 100 errors</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Account Sync Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Account Sync Status
              </CardTitle>
              <CardDescription>Last sync times and current status</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                {syncStatuses.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Database className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No email accounts connected</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {syncStatuses.map((status) => (
                      <div key={status.accountId} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="capitalize">
                                {status.provider}
                              </Badge>
                              <Badge variant="outline" className={getStatusColor(status.syncStatus)}>
                                {status.syncStatus}
                              </Badge>
                            </div>
                            <p className="text-sm font-medium truncate">{status.emailAddress}</p>
                            <p className="text-xs text-muted-foreground">ID: {status.accountId}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => triggerSync(status.accountId, status.provider)}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Sync
                          </Button>
                        </div>

                        <Separator className="my-3" />

                        <div className="space-y-2 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Last Sync:</span>
                            <span className="font-medium flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(status.lastSyncAt)}
                            </span>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Sync Enabled:</span>
                            <span className="font-medium flex items-center gap-1">
                              {status.syncEnabled ? (
                                <CheckCircle className="h-3 w-3 text-green-500" />
                              ) : (
                                <AlertCircle className="h-3 w-3 text-red-500" />
                              )}
                              {status.syncEnabled ? 'Yes' : 'No'}
                            </span>
                          </div>

                          {status.tokenExpiresAt && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Token Expires:</span>
                              <span className="font-medium">{formatTime(status.tokenExpiresAt)}</span>
                            </div>
                          )}

                          {status.historyId && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">History ID:</span>
                              <span className="font-mono text-[10px]">{status.historyId.substring(0, 16)}...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Sync Errors */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                Sync Errors
              </CardTitle>
              <CardDescription>Recent sync failures and issues</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                {syncErrors.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No errors found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {syncErrors.map((error) => (
                      <div key={error.id} className="p-3 border border-red-500/20 rounded-lg bg-red-500/5">
                        <div className="flex items-start gap-2 mb-2">
                          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs bg-red-500/10 text-red-600 border-red-500/20">
                                {error.errorType}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {formatTime(error.createdAt)}
                              </span>
                            </div>
                            <p className="text-sm font-medium mb-1">{error.errorMessage}</p>
                            <p className="text-xs text-muted-foreground">Account: {error.accountId}</p>
                            {error.errorDetails && (
                              <details className="mt-2">
                                <summary className="text-xs cursor-pointer hover:text-foreground">
                                  Show details
                                </summary>
                                <pre className="mt-2 p-2 bg-background rounded text-[10px] overflow-x-auto">
                                  {error.errorDetails}
                                </pre>
                              </details>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
    </div>
  );
};

export default SyncAdmin;
